import electron from "../electron";
import { WEBAPP_VERSION } from "../mwConstants";
import { isDialogSourceNativeMacApp } from "../nativeMacApp";
import { parseUrlParams } from "./url";

enum OS {
    IOS = "ios",
    Android = "android",
    Mac = "mac",
    Windows = "win",
    Linux = "linux",
    Unknown = "unknown"
}

enum Browser {
    Edge = "edge",
    Opera = "opera",
    Firefox = "firefox",
    Chrome = "chrome",
    Safari = "safari",
    Unknown = "unknown"
}

enum DeviceType {
    Mobile = "mobile",
    Desktop = "desktop"
}

enum AppClient {
    Web = "Web",
    Mac = "Electron macOS",
    Windows = "Windows"
}

enum AppType {
    WebApp = "webApp",
    MacApp = "macApp",
    WindowsApp = "windowsApp"
}

function getOS(): string {
    if (/ipad|iphone/i.test(navigator.userAgent)) {
        return OS.IOS;
    }

    if (/android/i.test(navigator.userAgent)) {
        return OS.Android;
    }

    if (/Macintosh/.test(navigator.userAgent)) {
        return OS.Mac;
    }

    if (/Windows/.test(navigator.userAgent)) {
        return OS.Windows;
    }

    if (/Linux/.test(navigator.userAgent) || /X11/.test(navigator.userAgent)) {
        return OS.Linux;
    }

    return OS.Unknown;
}

function getDevice(): string {
    if (/Mobi/.test(navigator.userAgent)) {
        return DeviceType.Mobile;
    }

    return DeviceType.Desktop;
}

function getBrowser(): Browser {
    if (/Edg/.test(navigator.userAgent)) {
        return Browser.Edge;
    }

    if (/OPR/.test(navigator.userAgent)) {
        return Browser.Opera;
    }

    if (/Firefox/.test(navigator.userAgent)) {
        return Browser.Firefox;
    }

    if (/Chrome/.test(navigator.userAgent)) {
        return Browser.Chrome;
    }

    if (/Safari/.test(navigator.userAgent)) {
        return Browser.Safari;
    }

    return Browser.Unknown;
}

function isMacOS(): boolean {
    return getOS() === OS.Mac;
}

function isWindows(): boolean {
    return getOS() === OS.Windows;
}

function isiOS(): boolean {
    return getOS() === OS.IOS;
}

function isAndroid(): boolean {
    return getOS() === OS.Android;
}

/**
 * Gets app version from user agent
 * @returns version or "unknown"
 */
function getAppVersion(): string {
    if (!electron) {
        return WEBAPP_VERSION;
    }

    const windowsAppRegex = /Zeplin(?: Windows v|\/)(\d+\.\d+\.\d+)/;
    const windowsAppMatch = navigator.userAgent.match(windowsAppRegex);

    if (windowsAppMatch) {
        return windowsAppMatch[1];
    }

    const macAppRegex = /Zeplin(?: Macintosh v|\/)(\d+\.\d+\.\d+)/;
    const macAppMatch = navigator.userAgent.match(macAppRegex);

    if (macAppMatch) {
        return macAppMatch[1];
    }

    return "unknown";
}

function getAppClient(): AppClient {
    if (!electron) {
        return AppClient.Web;
    }

    if (isMacOS()) {
        return AppClient.Mac;
    }

    return AppClient.Windows;
}

function getAppType(): AppType {
    const urlParams = parseUrlParams(location.search);

    // This is a special case where native mac app opens web app in a webview to display a dialog.
    const isNativeMacApp = isDialogSourceNativeMacApp(urlParams);
    if (isNativeMacApp) {
        return AppType.MacApp;
    }

    if (!electron) {
        return AppType.WebApp;
    }

    if (isMacOS()) {
        return AppType.MacApp;
    }

    return AppType.WindowsApp;
}

function toURI(path: string): string | null {
    if (!path || path.length === 0) {
        return null;
    }

    const localPath = path.replace(/^[\\/]|[\\/]$/g, "");
    return localPath.replace(/\\/g, "/");
}

function isMobileViewport(): boolean {
    return window.matchMedia("(max-width: 767px)").matches;
}

function getDownloadLink(): string {
    const userOS = getOS();
    if (userOS === OS.Mac) {
        return "https://zpl.io/download-mac";
    } else if (userOS === OS.Windows) {
        return "https://zpl.io/download-windows-64";
    }
    return "https://zpl.io/download";
}

export {
    OS,
    Browser,
    DeviceType,
    AppType,
    getOS,
    getDevice,
    getBrowser,
    isMacOS,
    isWindows,
    isiOS,
    isAndroid,
    getAppVersion,
    getAppClient,
    getAppType,
    toURI,
    isMobileViewport,
    getDownloadLink
};
